/**
 * AttendeePreferenceCard - Exact match to the user's screenshot design
 * Shows attendee info for events with proper styling
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, Edit3, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AttendeePreferenceCard - Main component matching the screenshot exactly
 * 
 * @param {Object} props
 * @param {Object} props.event - Event data (name, cover_image, etc.)
 * @param {Object} props.user - User data (name, profile_photo, wallet_id)
 * @param {Object} props.preferences - User preferences (accessibility, interests)
 * @param {string} props.cardColor - Card header color (defaults to dark purple)
 * @param {boolean} props.isHost - Whether user is a host
 * @param {boolean} props.isRsvp - Whether user has RSVP'd
 * @param {boolean} props.isOwner - Whether current user owns this card (can edit)
 * @param {function} props.onEditPreferences - Callback when edit clicked
 * @param {function} props.onCantMakeIt - Callback for cancellation
 * @param {function} props.onDone - Callback when done clicked
 */
export const AttendeePreferenceCard = ({ 
  event, 
  user, 
  preferences = {}, 
  cardColor = "#1a1a2e",
  isHost = false,
  isRsvp = false,
  isOwner = false,
  labels = [], // Array of label strings like ["Speaker/Panelist", "Host", "Pre-Invite"]
  showVenueContext = false, // Show "What the venue sees" text
  onEditPreferences,
  onCantMakeIt,
  onDone
}) => {
  // Extract preferences
  const invisibleDisabilities = preferences?.accessibility?.notes || 
    preferences?.accessibility?.invisible_disabilities || 
    preferences?.accessibility?.needs?.filter(n => 
      ['vocal_tics', 'sensory_sensitivity', 'anxiety', 'adhd', 'autism'].includes(n)
    ).map(n => {
      const labels = {
        vocal_tics: "May have involuntary vocal expressions",
        sensory_sensitivity: "May be sensitive to loud sounds or bright lights",
        anxiety: "May need quiet space or breaks",
        adhd: "May need movement breaks",
        autism: "May have specific sensory needs"
      };
      return labels[n] || n;
    }).join(", ");
  
  const dietary = preferences?.dietary || [];
  const interests = preferences?.events?.interests || 
    preferences?.interests || 
    [];
  const inspoImages = preferences?.inspo || [];
  
  // Additional preferences (hair, nails, style, vibe)
  const hairPrefs = preferences?.hair || {};
  const nailsPrefs = preferences?.nails || {};
  const stylePrefs = preferences?.style || [];
  const vibePrefs = preferences?.vibe || [];
  
  // Check if user has ANY preferences
  const hasPreferences = 
    invisibleDisabilities ||
    interests.length > 0 || 
    dietary.length > 0 || 
    inspoImages.length > 0 ||
    Object.keys(hairPrefs).length > 0 ||
    Object.keys(nailsPrefs).length > 0 ||
    stylePrefs.length > 0 ||
    vibePrefs.length > 0;
  
  // Get cover image
  const coverImage = event?.cover_image_url || event?.cover_image;
  
  // Label colors mapping
  const labelColors = {
    "Speaker/Panelist": "bg-pink-500 text-white",
    "Speaker": "bg-pink-500 text-white",
    "Panelist": "bg-pink-500 text-white",
    "Host": "bg-purple-600 text-white",
    "Pre-Invite": "bg-purple-500 text-white",
    "VIP": "bg-amber-500 text-white",
    "Press": "bg-blue-500 text-white",
    "Team": "bg-indigo-500 text-white"
  };
  
  return (
    <div className="w-full max-w-sm mx-auto bg-white rounded-2xl overflow-hidden shadow-xl">
      {/* Header with Event Name - Dark gradient background */}
      <div 
        className="p-4 text-white relative"
        style={{ 
          background: `linear-gradient(135deg, ${cardColor} 0%, #2d2d44 100%)`,
          minHeight: '120px'
        }}
      >
        {/* Decorative texture overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }}
        />
        
        {/* Attendee Card Badge + Event Logo */}
        <div className="flex items-center gap-2 mb-3 relative z-10">
          {coverImage ? (
            <img 
              src={coverImage.startsWith('/') ? `${API}${coverImage}` : coverImage}
              alt="" 
              className="w-10 h-10 rounded-lg object-cover border-2 border-white/20"
            />
          ) : (
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-xs border-2 border-white/20"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
            >
              {event?.name?.split(' ').map(w => w[0]).join('').slice(0, 4)}
            </div>
          )}
          <Badge className="bg-purple-500/80 text-white text-[10px] uppercase tracking-wide">
            Attendee Card
          </Badge>
        </div>
        
        {/* Event Name */}
        <h2 className="text-lg font-bold relative z-10">{event?.name || "Event"}</h2>
      </div>
      
      {/* User Info Section */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 ring-2 ring-purple-100">
            {user?.profile_photo_url ? (
              <img 
                src={user.profile_photo_url} 
                alt="" 
                className="w-full h-full object-cover" 
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100">
                <User className="w-7 h-7 text-purple-400" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{user?.name || "Guest"}</span>
            </div>
            {/* Role Labels - matching screenshot design */}
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {labels.map((label, idx) => (
                <Badge 
                  key={idx} 
                  className={`text-[10px] px-2 py-0.5 ${labelColors[label] || "bg-gray-500 text-white"}`}
                >
                  {label}
                </Badge>
              ))}
              {isHost && !labels.includes("Host") && (
                <Badge className="bg-purple-600 text-white text-[10px] px-2 py-0.5">Host</Badge>
              )}
              {isRsvp && !isHost && !labels.some(l => l.toLowerCase().includes('rsvp')) && (
                <Badge className="bg-green-500 text-white text-[10px] px-2 py-0.5">RSVP'd</Badge>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Venue Context Note */}
      {showVenueContext && (
        <div className="px-4 py-2 bg-gray-50 border-b">
          <p className="text-xs text-gray-500 font-medium">What the venue sees</p>
        </div>
      )}
      
      {/* Preferences Content */}
      <div className="px-4 py-4 space-y-4">
        {/* Invisible Disabilities */}
        {invisibleDisabilities && (
          <div className="bg-purple-50 rounded-lg p-3">
            <p className="text-[10px] font-semibold text-purple-600 uppercase tracking-wide mb-1">
              Accessibility Notes
            </p>
            <p className="text-sm text-gray-700">{invisibleDisabilities}</p>
          </div>
        )}
        
        {/* Dietary Restrictions */}
        {dietary.length > 0 && (
          <div className="bg-orange-50 rounded-lg p-3">
            <p className="text-[10px] font-semibold text-orange-600 uppercase tracking-wide mb-2">
              Dietary
            </p>
            <div className="flex flex-wrap gap-2">
              {dietary.map((item, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Interests */}
        {interests.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Interests
            </p>
            <div className="flex flex-wrap gap-2">
              {interests.map((interest, i) => (
                <span
                  key={i}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    i % 2 === 0 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-teal-100 text-teal-700'
                  }`}
                >
                  {interest}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Linked Inspo Images with Source Badges */}
        {inspoImages.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Inspo
            </p>
            <div className="grid grid-cols-3 gap-2">
              {inspoImages.slice(0, 6).map((inspo, idx) => {
                const imgUrl = typeof inspo === 'string' ? inspo : inspo.image_url || inspo.url;
                const source = typeof inspo === 'object' ? inspo.source : null;
                return (
                  <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative group">
                    <img 
                      src={imgUrl} 
                      alt="" 
                      className="w-full h-full object-cover" 
                    />
                    {/* Source badge */}
                    {source && (
                      <div className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-medium shadow-sm ${
                        source === 'host_curated' ? 'bg-purple-500 text-white' : 'bg-gray-800/80 text-white'
                      }`}>
                        {source === 'host_curated' ? 'From Host' : 'Uploaded'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Hair Preferences */}
        {Object.keys(hairPrefs).length > 0 && hairPrefs.hair_type && (
          <div className="bg-pink-50 rounded-lg p-3">
            <p className="text-[10px] font-semibold text-pink-600 uppercase tracking-wide mb-2">
              Hair
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              {hairPrefs.hair_type && (
                <span className="px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">
                  {hairPrefs.hair_type}
                </span>
              )}
              {hairPrefs.hair_texture && (
                <span className="px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">
                  {hairPrefs.hair_texture}
                </span>
              )}
              {hairPrefs.hair_length && (
                <span className="px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">
                  {hairPrefs.hair_length}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Style Preferences */}
        {stylePrefs.length > 0 && (
          <div className="bg-violet-50 rounded-lg p-3">
            <p className="text-[10px] font-semibold text-violet-600 uppercase tracking-wide mb-2">
              Style
            </p>
            <div className="flex flex-wrap gap-2">
              {stylePrefs.slice(0, 5).map((style, i) => (
                <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-violet-100 text-violet-700">
                  {style}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Vibe Preferences */}
        {vibePrefs.length > 0 && (
          <div className="bg-amber-50 rounded-lg p-3">
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide mb-2">
              Vibe
            </p>
            <div className="flex flex-wrap gap-2">
              {vibePrefs.slice(0, 5).map((vibe, i) => (
                <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                  {vibe}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Empty State - only show if NO preferences at all */}
        {!hasPreferences && (
          <div className="text-center py-4 text-gray-400">
            <p className="text-sm">No preferences set for this event</p>
          </div>
        )}
        
        {/* Transparency Note */}
        <p className="text-xs text-center text-gray-400 pt-2">
          This is what the venue sees
        </p>
      </div>
      
      {/* Action Buttons */}
      <div className="px-4 pb-4 space-y-2">
        <div className="flex gap-2">
          {/* Edit button only shows for card owner */}
          {isOwner && onEditPreferences && (
            <Button 
              variant="outline" 
              className="flex-1 text-gray-900 border-gray-300 hover:bg-gray-100"
              onClick={onEditPreferences}
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit Preferences
            </Button>
          )}
          {/* Can't Make It only shows for owner with RSVP */}
          {isOwner && onCantMakeIt && isRsvp && (
            <Button 
              variant="outline" 
              className="text-gray-600 border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
              onClick={onCantMakeIt}
            >
              Can't Make It
            </Button>
          )}
        </div>
        <Button 
          className="w-full bg-black hover:bg-gray-900 text-white"
          onClick={onDone}
        >
          Done
        </Button>
      </div>
    </div>
  );
};

/**
 * AttendeePreferenceCardModal - Modal wrapper
 */
export const AttendeePreferenceCardModal = ({
  isOpen,
  onClose,
  event,
  user,
  preferences,
  cardColor,
  isHost,
  isRsvp,
  isOwner = false,
  labels = [],
  showVenueContext = false,
  onEditPreferences,
  onCantMakeIt
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="p-0 max-w-sm bg-transparent border-0 shadow-none">
        <AttendeePreferenceCard
          event={event}
          user={user}
          preferences={preferences}
          cardColor={cardColor}
          isHost={isHost}
          isRsvp={isRsvp}
          isOwner={isOwner}
          labels={labels}
          showVenueContext={showVenueContext}
          onEditPreferences={onEditPreferences}
          onCantMakeIt={onCantMakeIt}
          onDone={onClose}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AttendeePreferenceCard;
